import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CareerSitePreview,
  CareerSitePreviewInput,
  CareerSiteSettingsView,
  JobDetail,
  JobOpeningStatus,
  JobOverview,
  JobPostingsView,
  PublishPostingRequest,
  RecordSpendRequest,
  ReferableJob,
  ReferralLinkView,
  UpdateCareerSiteSettingsInput,
  UpdatePostingInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../../lib/api-client';
import { JOBS_KEY, jobKey } from './jobs-api';

// S13: JOB-10, JOB-11, POST-01, POST-04, ADMIN-12, F6.05. Everything about one job
// hangs under its `['jobs', 'detail', id]` key, which the API's realtime hint
// (`['jobs']`) covers — a colleague publishing a posting refreshes this panel.

const REFERRALS_KEY = ['referrals'] as const;
const CAREER_SITE_KEY = ['admin', 'career-site'] as const;

function postingsKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'postings'];
}

function overviewKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'overview'];
}

export function useJobOverview(jobId: string): UseQueryResult<JobOverview, ApiError> {
  return useQuery<JobOverview, ApiError>({
    queryKey: overviewKey(jobId),
    queryFn: () => apiGet<JobOverview>(`/jobs/${jobId}/overview`),
    placeholderData: (previous) => previous,
  });
}

export function useJobPostings(jobId: string, isEnabled = true): UseQueryResult<JobPostingsView, ApiError> {
  return useQuery<JobPostingsView, ApiError>({
    queryKey: postingsKey(jobId),
    queryFn: () => apiGet<JobPostingsView>(`/jobs/${jobId}/postings`),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

export type PostingChange =
  | { readonly kind: 'publish'; readonly body: PublishPostingRequest }
  | { readonly kind: 'update'; readonly postingId: string; readonly body: UpdatePostingInput }
  | { readonly kind: 'unpublish' | 'republish'; readonly postingId: string; readonly version: number }
  | { readonly kind: 'spend'; readonly postingId: string; readonly body: RecordSpendRequest };

/**
 * Every POST-01 change returns the whole panel, which is seeded straight into the
 * cache; the invalidation that follows is awaited before the caller clears its
 * pending state, so nothing jumps back (CLAUDE.md §5a rule 3).
 */
export function useChangePosting(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<JobPostingsView, ApiError, PostingChange>({
    mutationFn: (change) => {
      switch (change.kind) {
        case 'publish':
          return apiPost<JobPostingsView>(`/jobs/${jobId}/postings`, change.body);
        case 'update':
          return apiPatch<JobPostingsView>(`/postings/${change.postingId}`, change.body);
        case 'unpublish':
        case 'republish':
          return apiPost<JobPostingsView>(`/postings/${change.postingId}/${change.kind}`, { version: change.version });
        case 'spend':
          return apiPost<JobPostingsView>(`/postings/${change.postingId}/spend`, change.body);
      }
    },
    onSuccess: async (panel) => {
      queryClient.setQueryData(postingsKey(jobId), panel);
      await queryClient.invalidateQueries({ queryKey: overviewKey(jobId) });
    },
  });
}

export type LifecycleChange =
  | { readonly kind: 'hold'; readonly reason: string }
  | { readonly kind: 'resume' }
  | { readonly kind: 'reopen'; readonly reason?: string }
  | { readonly kind: 'reopen-to-candidates' };

/** Where each move lands, so the status changes on screen before the server answers. */
function statusAfter(change: LifecycleChange): JobOpeningStatus {
  switch (change.kind) {
    case 'hold':
      return 'on_hold';
    case 'resume':
    case 'reopen-to-candidates':
      return 'open';
    case 'reopen':
      return 'reopened';
  }
}

/** The hold/resume buttons follow the status at once; the server's list replaces this when it answers. */
export function actionsAfter(actions: JobDetail['actions'], change: LifecycleChange): JobDetail['actions'] {
  if (change.kind === 'hold') return [...actions.filter((action) => action !== 'hold' && action !== 'resume'), 'resume'];
  if (change.kind === 'resume') return [...actions.filter((action) => action !== 'resume' && action !== 'hold'), 'hold'];
  return actions;
}

/**
 * F5.15–F5.17. Acts first (CLAUDE.md §5a rule 1): the status moves at once, rolls back
 * visibly if the server refuses, and the server's job replaces it when it answers.
 */
export function useJobLifecycle(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    JobDetail,
    ApiError,
    { readonly version: number; readonly change: LifecycleChange },
    { readonly previous: JobDetail | undefined }
  >({
    mutationFn: ({ version, change }) => {
      const { kind, ...rest } = change;
      return apiPost<JobDetail>(`/jobs/${jobId}/${kind}`, { version, ...rest });
    },
    onMutate: async ({ change }) => {
      await queryClient.cancelQueries({ queryKey: jobKey(jobId), exact: true });
      const previous = queryClient.getQueryData<JobDetail>(jobKey(jobId));
      if (previous) {
        queryClient.setQueryData<JobDetail>(jobKey(jobId), {
          ...previous,
          status: statusAfter(change),
          actions: actionsAfter(previous.actions, change),
          statusReason: 'reason' in change ? (change.reason ?? null) : previous.statusReason,
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(jobKey(jobId), context.previous);
    },
    onSuccess: async (job) => {
      queryClient.setQueryData(jobKey(job.id), job);
      await queryClient.invalidateQueries({ queryKey: JOBS_KEY });
    },
  });
}

// --- F6.05 --------------------------------------------------------------------------------

export function useReferableJobs(): UseQueryResult<readonly ReferableJob[], ApiError> {
  return useQuery<readonly ReferableJob[], ApiError>({
    queryKey: REFERRALS_KEY,
    queryFn: () => apiGet<readonly ReferableJob[]>('/referrals'),
    placeholderData: (previous) => previous,
  });
}

export function useReferralLink() {
  const queryClient = useQueryClient();
  return useMutation<ReferralLinkView, ApiError, string>({
    mutationFn: (jobId) => apiPost<ReferralLinkView>(`/referrals/${jobId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REFERRALS_KEY });
    },
  });
}

// --- ADMIN-12 and the §L2 name table ---------------------------------------------------------

/** `isEnabled` false for someone who may not open the page: nothing is asked of the server (review W15). */
export function useCareerSiteSettings(isEnabled = true): UseQueryResult<CareerSiteSettingsView, ApiError> {
  return useQuery<CareerSiteSettingsView, ApiError>({
    queryKey: CAREER_SITE_KEY,
    queryFn: () => apiGet<CareerSiteSettingsView>('/admin/career-site'),
    enabled: isEnabled,
  });
}

const CAREER_PREVIEW_KEY = [...CAREER_SITE_KEY, 'preview'] as const;
/** A preview of the same draft is the same picture for this long; today's roles rarely change faster. */
const PREVIEW_STALE_MS = 30_000;

/**
 * ADMIN-12's live preview (Admin redesign 2026-11-02): the public front page drawn from the settings as typed. The
 * last picture stays up while the next is drawn (CLAUDE.md §5a rule 2). Null asks for nothing.
 */
export function useCareerSitePreview(draft: CareerSitePreviewInput | null): UseQueryResult<CareerSitePreview, ApiError> {
  return useQuery<CareerSitePreview, ApiError>({
    queryKey: [...CAREER_PREVIEW_KEY, draft],
    queryFn: () => apiPost<CareerSitePreview>('/admin/career-site/preview', draft),
    enabled: draft !== null,
    placeholderData: (previous) => previous,
    staleTime: PREVIEW_STALE_MS,
  });
}

/** "Someone else saved first" (the `version` check). */
const HTTP_CONFLICT = 409;

export type CareerSiteChange =
  | { readonly kind: 'settings'; readonly body: UpdateCareerSiteSettingsInput }
  | { readonly kind: 'logo'; readonly file: File }
  | { readonly kind: 'remove-logo' };

export function useChangeCareerSite() {
  const queryClient = useQueryClient();
  return useMutation<CareerSiteSettingsView, ApiError, CareerSiteChange>({
    mutationFn: (change) => {
      if (change.kind === 'settings') return apiPut<CareerSiteSettingsView>('/admin/career-site', change.body);
      if (change.kind === 'remove-logo') return apiDelete<CareerSiteSettingsView>('/admin/career-site/logo');
      const form = new FormData();
      form.append('logo', change.file);
      return apiPut<CareerSiteSettingsView>('/admin/career-site/logo', form);
    },
    onSuccess: async (view, change) => {
      queryClient.setQueryData(CAREER_SITE_KEY, view);
      // A logo saves at once, so the preview redraws with the logo that is up now.
      if (change.kind !== 'settings') await queryClient.invalidateQueries({ queryKey: CAREER_PREVIEW_KEY });
    },
    onError: async (error) => {
      // Someone else saved first: fetch theirs, so the page can offer to bring their changes in beside these.
      if (error.status === HTTP_CONFLICT) await queryClient.invalidateQueries({ queryKey: CAREER_SITE_KEY, exact: true });
    },
  });
}
